import React, { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Theme, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { useMutation, useReactiveVar } from '@apollo/client';
import { assistantQuestionVar } from 'apollo/reactive-variables/help-drawer';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { assistantChatTemplate } from 'apollo/templates/assistant';
import { submitSupportTicketTemplate } from 'apollo/templates/zendesk';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { conversationTranscript } from './conversation-transcript';
import { AssistantChatMessage } from 'models/assistant';
import { maskPersonalIdentityNumbers } from 'utils/mask-personal-identity-numbers';
import InfoText from 'components/shared/info-text.component';
import { CONSTANTS } from 'config/constants';
import AssistantMessageBubble from './assistant-message-bubble.component';
import AssistantArticlePanel from './assistant-article-panel.component';
import { useAssistantReplyStream } from './use-assistant-reply-stream';
import { recentErrors } from 'apollo/recent-errors';

/** The conversation keeps a readable width; with an article open beside it, they share the page. */
const CHAT_MAX_WIDTH = 1000;
/** With an article open, the chat keeps this width on the left and the article gets the rest. */
const CHAT_WIDTH_BESIDE_ARTICLE = 640;

/** The chat reads 2px larger than the app's body2 text: messages, links, intro and input. */
export const LARGER_CHAT_TEXT = {
    '& .MuiTypography-body2, & .MuiLink-root, & .MuiInputBase-input, & .MuiTypography-caption': {
        fontSize: (theme: Theme) => `calc(${theme.typography.body2.fontSize} + 2px)`,
    },
};

type AssistantChatProps = {
    messages: AssistantChatMessage[];
    onMessagesChange: (messages: AssistantChatMessage[]) => void;
    /** The stored conversation the next question continues; none for a new chat. */
    conversationId?: string;
    onConversationIdChange: (conversationId: string) => void;
    /** The drawer is at full page width: articles open beside the chat instead of on top of it. */
    isWide: boolean;
    /** Opens a page the assistant linked (in-app navigation only). */
    onNavigate: (path: string) => void;
    /** Points at a UI element on the current page. */
    onShowUiElement: (name: string) => void;
};

/** DD-3780: the help assistant. Read-only, and it never sees patient data. */
const AssistantChat: FC<AssistantChatProps> = ({
    messages,
    onMessagesChange,
    conversationId,
    onConversationIdChange,
    isWide,
    onNavigate,
    onShowUiElement,
}: AssistantChatProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const [question, setQuestion] = useState('');
    const [failed, setFailed] = useState(false);
    const [openArticleId, setOpenArticleId] = useState<string>();
    // The answer to reveal line by line: only one that just arrived, never on re-render.
    const [revealIndex, setRevealIndex] = useState<number>();
    const endOfMessages = useRef<HTMLDivElement>(null);
    const { previewText, activity, beginStream, endStream, hasStreamedText } = useAssistantReplyStream();
    const location = useLocation();

    const [assistantChat, { loading }] = useMutation(assistantChatTemplate);
    const [submitSupportTicket] = useMutation(submitSupportTicketTemplate);

    /** The user pressed Send: their edited report plus the conversation, as a Zendesk ticket. */
    const sendProblemReport = async (report: string): Promise<boolean> => {
        const currentUser = currentUserVar();
        try {
            const { data } = await submitSupportTicket({
                variables: {
                    name: `${currentUser.name.firstName} ${currentUser.name.lastName}`,
                    email: currentUser.email,
                    message: `${report}\n\n${conversationTranscript(messages, location.pathname, recentErrors())}`,
                },
            });
            return data?.submitSupportTicket === 'OK';
        } catch {
            return false;
        }
    };

    useEffect(() => {
        endOfMessages.current?.scrollIntoView?.({ behavior: 'smooth' });
    }, [messages, loading, previewText]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        await ask(question);
    };

    // DD-3780: a question handed over from elsewhere (the error offer) is asked once the chat opens.
    const pendingQuestion = useReactiveVar(assistantQuestionVar);
    useEffect(() => {
        if (pendingQuestion && !loading) {
            assistantQuestionVar(undefined);
            ask(pendingQuestion);
        }
    }, [pendingQuestion]);

    const ask = async (rawQuestion: string) => {
        const text = maskPersonalIdentityNumbers(rawQuestion.trim());
        if (!text || loading) {
            return;
        }
        const conversation: AssistantChatMessage[] = [...messages, { role: 'USER', text }];
        onMessagesChange(conversation);
        setQuestion('');
        setFailed(false);
        try {
            const { data } = await assistantChat({
                variables: {
                    messages: conversation.map(({ role, text }) => ({ role, text })),
                    // White-labeled clinics need the lab they work with, as on the order list.
                    currentLabId: Cookies.get(CONSTANTS.LAB_ID_KEY),
                    streamId: beginStream(),
                    // The page the user has open, so questions are answered for that page.
                    currentPage: `${location.pathname}${location.hash}`,
                    // The errors the user just got, so "why did that fail?" can be answered.
                    recentErrors: recentErrors(),
                    // DD-3780: each turn is stored on the server; follow-ups go to the same conversation.
                    conversationId,
                },
            });
            // A preview already showed the words as they were written; reveal only a burst.
            setRevealIndex(hasStreamedText() ? undefined : conversation.length);
            const { conversationId: storedConversationId, ...reply } = data.assistantChat;
            onMessagesChange([...conversation, { role: 'ASSISTANT', ...reply }]);
            if (storedConversationId) {
                onConversationIdChange(storedConversationId);
            }
        } catch {
            setFailed(true);
        } finally {
            endStream();
        }
    };

    const conversation = (
        <Box display="flex" flexDirection="column" height="100%" sx={LARGER_CHAT_TEXT}>
            <Box
                flexGrow={1}
                sx={{
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    px: 2,
                }}
            >
                {messages.length === 0 ? (
                    <Typography variant="body2" color="grey" my={2}>
                        {translate('help-drawer:ASSISTANT_INTRO')}
                    </Typography>
                ) : undefined}
                <Stack spacing={1.5} py={1}>
                    {messages.map((message, index) => (
                        <AssistantMessageBubble
                            key={index}
                            message={message}
                            reveal={index === revealIndex}
                            onArticleClicked={setOpenArticleId}
                            onNavigate={onNavigate}
                            onShowUiElement={onShowUiElement}
                            onSendProblemReport={sendProblemReport}
                        />
                    ))}
                    {loading && previewText ? (
                        <AssistantMessageBubble
                            message={{ role: 'ASSISTANT', text: previewText }}
                            onArticleClicked={setOpenArticleId}
                            onNavigate={onNavigate}
                            onShowUiElement={onShowUiElement}
                            onSendProblemReport={sendProblemReport}
                        />
                    ) : undefined}
                    {loading && !previewText ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="grey">
                                {translate(
                                    activity
                                        ? `help-drawer:ASSISTANT_ACTIVITY_${activity}`
                                        : 'help-drawer:ASSISTANT_THINKING',
                                )}
                            </Typography>
                        </Box>
                    ) : undefined}
                    {failed ? <Alert severity="error">{translate('help-drawer:ASSISTANT_FAILED')}</Alert> : undefined}
                    <div ref={endOfMessages} />
                </Stack>
            </Box>
            <InfoText text={translate('help-drawer:ASSISTANT_NO_PATIENT_DATA')} />
            <form onSubmit={handleSubmit}>
                <Box display="flex" gap={1} alignItems="center">
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={question}
                        placeholder={translate('help-drawer:ASSISTANT_PLACEHOLDER')}
                        onChange={(event) => setQuestion(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                handleSubmit(event);
                            }
                        }}
                        inputProps={{ maxLength: 4000 }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!question.trim() || loading}
                        aria-label={translate('help-drawer:ASSISTANT_SEND')}
                        sx={{ minWidth: 48, height: 40, flexShrink: 0 }}
                    >
                        <SendIcon fontSize="small" />
                    </Button>
                </Box>
            </form>
        </Box>
    );

    const articlePanel = openArticleId ? (
        <AssistantArticlePanel
            articleId={openArticleId}
            isBesideChat={isWide}
            onArticleChange={setOpenArticleId}
            onClose={() => setOpenArticleId(undefined)}
        />
    ) : undefined;

    if (!isWide) {
        // Narrow drawer: the article covers the chat; the conversation stays mounted underneath.
        return (
            <Box height="100%" position="relative">
                <Box height="100%" sx={{ visibility: articlePanel ? 'hidden' : 'visible' }}>
                    {conversation}
                </Box>
                {articlePanel ? (
                    <Box position="absolute" sx={{ inset: 0, bgcolor: 'background.paper' }}>
                        {articlePanel}
                    </Box>
                ) : undefined}
            </Box>
        );
    }
    return (
        <Box display="flex" gap={2} height="100%" justifyContent={articlePanel ? 'flex-start' : 'center'}>
            <Box
                flex={articlePanel ? `0 0 ${CHAT_WIDTH_BESIDE_ARTICLE}px` : 1}
                maxWidth={CHAT_MAX_WIDTH}
                minWidth={0}
                height="100%"
            >
                {conversation}
            </Box>
            {articlePanel ? (
                <Box flex={1} minWidth={0} height="100%">
                    {articlePanel}
                </Box>
            ) : undefined}
        </Box>
    );
};

export default AssistantChat;
